import React, { useEffect, useState } from 'react';
import { Form, Input, Select, DatePicker, Button, Row, Col, Card, Descriptions, Modal } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CurrencyInput from './CurrencyInput';
import dayjs from 'dayjs';
import { Money, Receipt } from '@/types';
import { formatFirestoreTimestamp } from '@/utils/date';

interface ReceiptFormSectionProps {
  disabled?: boolean;
  receipts?: Receipt[];
  onReceiptsChange?: (receipts: Receipt[]) => void;
}

const ReceiptFormSection: React.FC<ReceiptFormSectionProps> = ({ 
  disabled = false, 
  receipts = [],
  onReceiptsChange 
}) => {
  const { t } = useTranslation(['common']);
  const [localReceipts, setLocalReceipts] = useState<Receipt[]>(receipts);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  const receiptStatuses = [
    { text: 'Completed', value: 'completed' },
    { text: 'Cancelled', value: 'cancelled' },
    { text: 'Failed', value: 'failed' },
  ];

  // Update local receipts when props change
  useEffect(() => {
    setLocalReceipts(receipts || []);
  }, [receipts]);

  const handleAddReceipt = () => {
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      const newReceipt: Receipt = {
        id: `temp_${Date.now()}`,
        name: values.name,
        status: values.status,
        amount: values.amount,
        date: values.date?.toDate() || new Date(),
        description: values.description
      };

      const updatedReceipts = [...localReceipts, newReceipt];
      setLocalReceipts(updatedReceipts);
      
      if (onReceiptsChange) {
        onReceiptsChange(updatedReceipts);
      }

      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  const handleRemoveReceipt = (receiptId: string) => {
    const updatedReceipts = localReceipts.filter(receipt => receipt.id !== receiptId);
    setLocalReceipts(updatedReceipts);
    
    if (onReceiptsChange) {
      onReceiptsChange(updatedReceipts);
    }
  };

  const formatMoney = (amount: Money) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: amount.currency || 'USD'
    }).format(amount.amount);
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button 
          type="primary" 
          onClick={handleAddReceipt}
          disabled={disabled}
          icon={<PlusOutlined />}
        >
          {t('common:addReceipt')}
        </Button>
      </div>

      {localReceipts && localReceipts.length > 0 ? (
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          {localReceipts.map((receipt) => (
            <Card 
              key={receipt.id} 
              size="small" 
              style={{ marginBottom: 16 }}
              extra={
                !disabled && (
                  <Button
                    type="text"
                    danger
                    icon={<MinusCircleOutlined />}
                    onClick={() => handleRemoveReceipt(receipt.id)}
                  >
                    {t('common:remove')}
                  </Button>
                )
              }
            >
              <Descriptions bordered column={2}>
                <Descriptions.Item label={t('common:name')}>
                  {receipt.name}
                </Descriptions.Item>
                <Descriptions.Item label={t('common:statusLabel')}>
                  <span style={{ 
                    color: receipt.status === 'completed' ? 'green' : 
                           receipt.status === 'cancelled' ? 'red' : 'orange' 
                  }}>
                    {receiptStatuses.find(s => s.value === receipt.status)?.text}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label={t('common:amount')}>
                  <span style={{ fontWeight: 500, color: '#1890ff' }}>
                    {formatMoney(receipt.amount)}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label={t('common:receiptDate')}>
                  {receipt.date ? dayjs(formatFirestoreTimestamp(receipt.date)).format('MMM DD, YYYY') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('common:description')} span={2}>
                  {receipt.description}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ))}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '24px' }}>
          {t('common:noReceipts')}
        </div>
      )}

      <Modal
        title={t('common:addReceipt')}
        open={isModalVisible}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        confirmLoading={false}
        width={600}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: 'completed',
            amount: { amount: 0, currency: 'USD' },
            date: dayjs(),
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t('common:name')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                <Input placeholder={t('common:name')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label={t('common:statusLabel')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                <Select
                  placeholder={t('common:select')}
                  options={receiptStatuses.map(s => ({ value: s.value, label: s.text }))}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="amount"
                label={t('common:amount')}
                rules={[
                  { required: true, message: t('common:fieldRequired') },
                  {
                    validator: (_, value) => {
                      if (!value || !value.amount || value.amount <= 0) {
                        return Promise.reject(new Error(t('common:invalid')));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <CurrencyInput />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="date"
                label={t('common:receiptDate')}
                rules={[{ required: true, message: t('common:fieldRequired') }]}
              >
                <DatePicker
                  placeholder={t('common:receiptDate')}
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="description"
            label={t('common:description')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <Input.TextArea
              placeholder={t('common:description')}
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReceiptFormSection; 